import { GridColDef } from "@mui/x-data-grid";
import { useContratosDispatch, useContratosState } from "./contratos-context";
import { ACTION_TYPES, initialDataRequest } from "./contratos-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IContratoResponse } from "../../../../model/admin-portal/response/contrato-response";
import { ITermPaggedRequest } from "../../../../model/admin-portal/request/term-pagged-request";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import { IDocumentoContratoRequest } from "../../../../model/admin-portal/request/documento-contrato-request";

export const useContratosController = () => {
    const state = useContratosState();
    const dispatch = useContratosDispatch();
    const { showMessage, showBackDrop } = useBaseLayoutController();

    const loadData = async (searchRequest: ITermPaggedRequest) => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listContratos(searchRequest);
            dispatch({ type: ACTION_TYPES.setDataPaginatedListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar contratos' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const onChangePagination = async (page: number, pageSize: number) => {
        const newSearch = { ...state.dataSearchRequest, page, pageSize };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const handleOnChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: { ...state.dataSearchRequest, term: e.target.value } });
    };

    const clearFields = async () => {
        const newSearch = { page: 0, pageSize: state.dataSearchRequest.pageSize, term: '' };
        dispatch({ type: ACTION_TYPES.setDataSearchRequest, payload: newSearch });
        await loadData(newSearch);
    };

    const loadLookups = async () => {
        try {
            const [tipos, situacoes, licitacoes, fornecedores] = await Promise.all([
                AdminPortalAppService.listTiposContrato(),
                AdminPortalAppService.listSituacoesContrato(),
                AdminPortalAppService.listLicitacoesLookup(),
                AdminPortalAppService.listFornecedoresLookup()
            ]);
            dispatch({ type: ACTION_TYPES.setTiposList, payload: tipos });
            dispatch({ type: ACTION_TYPES.setSituacoesList, payload: situacoes });
            dispatch({ type: ACTION_TYPES.setLicitacoesList, payload: licitacoes });
            dispatch({ type: ACTION_TYPES.setFornecedoresList, payload: fornecedores });
        } catch (error: any) {
            showMessage({ type: 'error', message: 'Erro ao carregar listas de apoio' });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getContrato(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });
            dispatch({
                type: ACTION_TYPES.setDataRequest, payload: {
                    id: response.id,
                    idLicitacao: response.idLicitacao,
                    idFornecedor: response.idFornecedor,
                    idTipoContrato: response.idTipoContrato,
                    idSituacaoContrato: response.idSituacaoContrato,
                    numContrato: response.numContrato,
                    numProcessoAdministrativo: response.numProcessoAdministrativo || '',
                    textoObjetoContrato: response.textoObjetoContrato,
                    valorContrato: response.valorContrato,
                    dtaAssinatura: response.dtaAssinatura,
                    dtaInicioVigencia: response.dtaInicioVigencia,
                    dtaFimVigencia: response.dtaFimVigencia,
                    dtaPublicacao: response.dtaPublicacao,
                    pncpUrl: response.pNCPUrl || '',
                    documentos: response.documentos?.map(d => ({ id: d.id, descricao: d.descricao, dtaPublicacao: d.dtaPublicacao, documentoUrl: d.documentoUrl })) || []
                }
            });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar contrato' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        const r = state.dataRequest;
        if (!r.idLicitacao || !r.idFornecedor || !r.idTipoContrato || !r.idSituacaoContrato) return false;
        if (!r.numContrato || r.numContrato.trim() === '') return false;
        if (!r.textoObjetoContrato || r.textoObjetoContrato.trim() === '') return false;
        if (!r.dtaAssinatura || !r.dtaInicioVigencia || !r.dtaFimVigencia || !r.dtaPublicacao) return false;
        if (!r.valorContrato || r.valorContrato <= 0) return false;
        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, [field]: value } });
    };

    const openForm = (row?: IContratoResponse) => {
        if (row) {
            getData(row.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialDataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateContrato(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Contrato atualizado com sucesso' : 'Contrato criado com sucesso'
            });
            closeForm();
            await loadData(state.dataSearchRequest);
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar contrato' });
        } finally {
            showBackDrop(false);
        }
    };

    //Cada filho é mantido só no estado local até o "Salvar" do Contrato inteiro - linhas novas usam id 0
    const saveDocumento = (doc: IDocumentoContratoRequest, index?: number) => {
        const documentos = [...state.dataRequest.documentos];
        if (index !== undefined) documentos[index] = doc; else documentos.push(doc);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const removeDocumento = (index: number) => {
        const documentos = state.dataRequest.documentos.filter((_, i) => i !== index);
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: { ...state.dataRequest, documentos } });
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            { field: 'numContrato', headerName: 'Nº Contrato', flex: 1, minWidth: 130 },
            { field: 'fornecedorName', headerName: 'Fornecedor', flex: 1, minWidth: 160 },
            { field: 'tipoContratoName', headerName: 'Tipo', flex: 1, minWidth: 130 },
            {
                field: 'situacaoContratoName', headerName: 'Situação', width: 140,
                renderCell: (params) => <Chip label={params.value} size="small" />
            },
            {
                field: 'valorContrato', headerName: 'Valor', width: 150, align: 'right', headerAlign: 'right',
                renderCell: (params) => params.value?.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            },
            {
                field: 'actions', headerName: 'Ações', width: 90, align: 'center', headerAlign: 'center', sortable: false,
                renderCell: (params) => (
                    <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                            <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openForm(params.row); }}>
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )
            }
        ];
    };

    return {
        ...state,
        loadData,
        onChangePagination,
        handleOnChangeSearch,
        clearFields,
        loadLookups,
        getData,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        save,
        getColumnsGrid,
        saveDocumento,
        removeDocumento
    };
};
